import React, {useMemo} from 'react';
import type {
  NameForm,
  NameVariantValue,
  PartNameVariant,
} from 'schemaDefinition/types';
import {useLocalization} from 'localization';
import {isLinkedToNameVariant} from 'services/agent';
import {Icon, Layout, Spacer} from 'components';
import type {BasePartProps} from './types';
import {usePreviewConfigurationContext} from '../contexts';
import {formatNameVariantValue} from '../functions';
import {useTextValue} from '../hooks';
import {LinkNameVariantButton} from './LinkNameVariantButton';
import {MultiValue} from './fields/MultiValue';

type PreviewNameVariantProps = BasePartProps<PartNameVariant>;

export const PreviewNameVariant: React.FC<PreviewNameVariantProps> = ({
  part,
  value,
  layoutDirection,
}) => {
  const {t} = useLocalization();
  const config = usePreviewConfigurationContext();
  const {nameVariants} = useMemo(() => {
    const values = value as NameVariantValue[];
    return {
      nameVariants: (values
        ? config.nameVariant?.excludeMainForm
          ? values.slice(1)
          : values
        : []
      ).map((val, idx) => ({
        ...val,
        nameForm: (!config.nameVariant?.excludeMainForm && idx === 0
          ? 'mainForm'
          : 'variant') as NameForm,
      })),
    };
  }, [config, value]);

  const TextValue = useTextValue();

  const linking = config.linking;

  return (
    <MultiValue
      part={part}
      value={nameVariants}
      layoutDirection={layoutDirection}
      renderValue={(key, singleValue) => {
        const isLinked =
          linking &&
          isLinkedToNameVariant(
            linking?.currentValue,
            linking.entityId,
            singleValue,
            singleValue.nameForm,
          );
        return linking ? (
          <Layout
            key={key}
            flex={1}
            horizontal
            alignItems="center"
            justifyContent={'space-between'}
            pb={0.5}>
            <Layout horizontal adjustCenter adjustLeft>
              {isLinked ? <Icon icon="CheckCircle" /> : null}
              {isLinked ? <Spacer size={1} /> : null}
              <TextValue key={key}>
                {formatNameVariantValue(
                  singleValue,
                  part.agentType,
                  'excludeId',
                  singleValue.nameForm === 'mainForm'
                    ? t('form.global.field.nameVariantMain.label')
                    : undefined,
                )}
              </TextValue>
            </Layout>
            <LinkNameVariantButton
              value={singleValue}
              linkingConfiguration={linking}
            />
          </Layout>
        ) : (
          <Layout>
            <TextValue key={key}>
              {formatNameVariantValue(
                singleValue,
                part.agentType,
                'excludeId',
                singleValue.nameForm === 'mainForm'
                  ? t('form.global.field.nameVariantMain.label')
                  : undefined,
              )}
            </TextValue>
          </Layout>
        );
      }}
    />
  );
};
